/**
 * Namuna ma'lumot.
 *
 * Qiymatlar HAQIQIY shakllarda: yacheyka kodlari TZ §5.1
 * formatida (`A{qator}-{stellaj}-{qavat}-{yacheyka}`), miqdorlar
 * matn sifatida (§7.2), holat nomlari servis qiymatlari bilan
 * bir xil. «Tovar 1» va «100» bilan sinalgan jadval haqiqiy nom
 * va uzun raqam kelganda buzilib ketardi.
 */

/** Takrorlanadigan tasodif — har ochilganda bir xil ko'rinish. */
function seeded(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

const SKUS = [
  { article: "4820024", name: "Chinni likopcha 24 sm, oq", pack: "Korobka (12 dona)" },
  { article: "4820091", name: "Stakan to'plami 6 dona, shaffof", pack: "Korobka (6 dona)" },
  { article: "4820155", name: "Choynak 1.2 l, ko'k gulli", pack: "Dona" },
  { article: "4820310", name: "Kosa 18 sm, to'plam 6 dona", pack: "Pallet" },
  { article: "4820402", name: "Piyola 200 ml, gulli", pack: "Korobka (24 dona)" },
  { article: "4820777", name: "Tarelka chuqur 22 sm, bej", pack: "Pallet" },
  { article: "4821004", name: "Qoshiq to'plami 12 dona, po'lat", pack: "Korobka (12 dona)" },
  { article: "4821190", name: "Kastryulka 3 l, qopqoqli", pack: "Dona" },
];

/**
 * Namunadagi tovarning artikuli va nomi — BITTA joyda.
 *
 * Nega shu yerda: qoldiq hisoboti (`fixtures.ts`) va tovarlar
 * ro'yxati (`entities.ts`) bir xil tovarlarni ko'rsatishi kerak.
 * Ilgari ularda ikki xil artikul bo'lgan va tovar kartasidagi
 * «Omborda» jadvali namuna rejimida HAR DOIM bo'sh chiqardi —
 * kartada ko'rsatilgan tovar qoldiqda umuman yo'q edi.
 */
export const SKU_COUNT = 36;

export function skuArticle(index: number): string {
  return `UY-${String(1040 + (index % SKU_COUNT) * 7)}`;
}

const SKU_NAMES = ["Kosa 12 sm", "Tarelka chuqur", "Choynak 1 l", "Piyola oq", "Lagan 32 sm", "Qoshiq to'plami"];

export function skuName(index: number): string {
  const position = index % SKU_COUNT;
  return SKU_NAMES[position] ?? `${SKU_NAMES[position % SKU_NAMES.length]} ${position + 1}`;
}

/** A korpus yacheyka kodi: qator-stellaj-qavat-yacheyka. */
function cellCode(rack: number, level: number, cell: number): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `A01-${pad(rack)}-${pad(level)}-${pad(cell)}`;
}

function lpCode(index: number): string {
  return `LP-${String(184000 + index).padStart(9, "0")}`;
}

function isoAgo(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString();
}

// ---------------------------------------------------------------------------
// Hisobotlar
// ---------------------------------------------------------------------------

export interface ReportFixture {
  columns: string[];
  rows: string[][];
}

export function stockReport(): ReportFixture {
  const random = seeded(7);
  const rows: string[][] = [];

  for (let index = 0; index < 48; index++) {
    const onHand = Math.round(random() * 400) + 12;
    const reserved = Math.round(onHand * random() * 0.4);
    const inTransit = random() > 0.8 ? Math.round(random() * 24) : 0;
    const blocked = random() > 0.92;

    rows.push([
      cellCode((index % 40) + 1, (index % 9) + 1, (index % 29) + 1),
      "A-STORAGE",
      "A",
      index % 5 === 0 ? "MX" : "OX",
      skuArticle(index),
      skuName(index),
      lpCode(index),
      index % 7 === 0 ? `LOT-2026-${100 + index}` : "",
      String(onHand),
      String(reserved),
      String(inTransit),
      String(onHand - reserved - inTransit),
      blocked ? "ha" : "yo'q",
      isoAgo(Math.round(random() * 4000)),
    ]);
  }

  return {
    columns: [
      "location_code", "zone_code", "warehouse_code", "location_function",
      "sku_article", "sku_name", "lp_id", "lot_ref",
      "on_hand_qty", "reserved_qty", "in_transit_qty", "available_qty",
      "blocked", "last_movement_at",
    ],
    rows,
  };
}

export function capacityReport(): ReportFixture {
  const random = seeded(19);
  const rows: string[][] = [];

  // Korpuslar va hududlar ARALASH: topologiya ekranidagi filtrlar
  // bitta qiymatli ro'yxatda hech narsa ajratmasdi.
  const corpus = ["A", "B", "C"];
  const areas = ["A", "B", "C", ""];

  for (let index = 0; index < 42; index++) {
    const capacity = index % 4 === 0 ? 0 : (index % 4) + 1;
    const occupied = capacity === 0 ? 0 : Math.round(random() * capacity);
    const blocked = random() > 0.9;
    const smallCell = capacity === 0;

    rows.push([
      `loc-${String(index).padStart(3, "0")}`,
      cellCode((index % 40) + 1, (index % 9) + 1, (index % 29) + 1),
      "A",
      `${corpus[index % 3]}-STORAGE`,
      index % 5 === 0 ? "MX" : "OX",
      areas[index % 4]!,
      smallCell ? "Y1" : "PL",
      // Y1/Y2 mayda terish yacheykalarida pallet o'rni YO'Q
      // (TZ §5.2) — bo'sh qoladi, nol emas.
      smallCell ? "" : String(capacity),
      String(occupied),
      smallCell ? "0" : String(capacity - occupied),
      // Mayda terish yacheykasining vazn chegarasi turdan keladi,
      // reyestrda ko'rsatilmaydi — bo'sh qoladi.
      smallCell ? "" : String(400 + index * 7),
      // Mayda terish yacheykasiga 55 kg dan kam yuk qo'yiladi;
      // pallet o'rni esa yuzlab kilogramm ko'taradi.
      String(Math.round(random() * (smallCell ? 70 : 500))),
      "ha",
      String(smallCell ? 1 : Math.round(random() * 4)),
      blocked ? "yo'q" : "ha",
      index % 17 === 0 ? "yo'q" : "ha",
      blocked ? "ha" : "yo'q",
    ]);
  }

  return {
    columns: [
      "location_id",
      "location_code", "warehouse_code", "zone_code", "location_function",
      "abc_zone",
      "cell_type", "capacity_pallets", "occupied_slots", "free_slots",
      "max_weight_kg", "stored_weight_kg", "weight_fits",
      "distinct_sku_count", "putaway_allowed", "is_active", "blocked",
    ],
    rows,
  };
}

const TASK_TYPES = ["PICK", "PUTAWAY", "REPLENISHMENT", "RECEIPT", "CONTROL", "COUNT"];
const TASK_STATUSES = [
  "IN_PROGRESS", "COMPLETED", "ASSIGNED", "WAITING_INVENTORY",
  "COMPLETED", "DISCREPANCY", "COMPLETED",
];
const WORKERS = ["Sardor A.", "Bekzod M.", "Jasur T.", "Aziz Q.", "Dilshod R.", "Otabek S."];

export function tasksReport(): ReportFixture {
  const random = seeded(31);
  const rows: string[][] = [];

  for (let index = 0; index < 54; index++) {
    const planned = Math.round(random() * 180) + 6;
    const status = TASK_STATUSES[index % TASK_STATUSES.length]!;
    const done = status === "COMPLETED";
    const short = status === "DISCREPANCY";
    const actual = done ? planned : short ? Math.round(planned * 0.8) : 0;

    rows.push([
      `TSK-${String(4700 + index).padStart(6, "0")}`,
      TASK_TYPES[index % TASK_TYPES.length]!,
      status,
      "A",
      WORKERS[index % WORKERS.length]!,
      index % 3 === 0 ? "PICKING" : "PUTAWAY",
      index % 4 === 0 ? `BUY-2026-${String(300 + index).padStart(5, "0")}` : "",
      String(planned),
      actual === 0 ? "" : String(actual),
      String((index % 5) + 1),
      short ? "ha" : "yo'q",
      status === "WAITING_INVENTORY" ? "INVENTORY_COUNT" : "",
      status === "WAITING_INVENTORY" ? "Inventarizatsiya kutilmoqda" : "",
      isoAgo(Math.round(random() * 2000) + 60),
      isoAgo(Math.round(random() * 1500)),
      done ? isoAgo(Math.round(random() * 900)) : "",
      String(Math.round(random() * 1800) + 120),
      String(Math.round(random() * 400)),
    ]);
  }

  return {
    columns: [
      "task_id", "task_type", "status", "warehouse_code", "assignee_id",
      "source_process", "order_id", "planned_qty", "actual_qty",
      "line_count", "has_shortage", "waiting_for", "stop_reason",
      "created_at", "started_at", "completed_at", "work_seconds", "wait_seconds",
    ],
    rows,
  };
}

const DISCREPANCY_TYPES = ["SHORTAGE", "EXCESS", "LP_MISMATCH", "SHORTAGE", "DAMAGE"];

export function discrepancyReport(): ReportFixture {
  const random = seeded(53);
  const rows: string[][] = [];

  for (let index = 0; index < 26; index++) {
    const planned = Math.round(random() * 90) + 10;
    const delta = Math.round(random() * 8) + 1;
    const resolved = random() > 0.55;
    const sku = SKUS[index % SKUS.length]!;

    rows.push([
      index % 2 === 0 ? "CONTROL" : "PICKING",
      DISCREPANCY_TYPES[index % DISCREPANCY_TYPES.length]!,
      sku.article,
      cellCode((index % 40) + 1, (index % 9) + 1, (index % 29) + 1),
      lpCode(index + 40),
      `BUY-2026-${String(300 + index).padStart(5, "0")}`,
      String(planned),
      String(planned - delta),
      String(delta),
      resolved ? "0" : String(delta),
      random() > 0.6 ? `BLK-${1000 + index}` : "",
      random() > 0.5 ? `CNT-${2000 + index}` : "",
      resolved ? "RESOLVED" : "OPEN",
      resolved ? "INVENTORY_CONFIRMED" : "",
      isoAgo(Math.round(random() * 3000) + 30),
      resolved ? isoAgo(Math.round(random() * 600)) : "",
    ]);
  }

  return {
    columns: [
      "process", "discrepancy_type", "sku_article", "location_code",
      "lp_id", "order_id", "planned_qty", "actual_qty", "delta_qty",
      "remaining_qty", "block_id", "count_task_id", "status",
      "resolution", "detected_at", "resolved_at",
    ],
    rows,
  };
}

export function incompleteOrdersReport(): ReportFixture {
  const random = seeded(89);
  const reasons = [
    "INVENTORY_COUNT", "BLOCKED_LOCATION", "SHORTAGE",
    "WAITING_MANAGER", "ALTERNATIVE_SEARCH",
  ];
  const rows: string[][] = [];

  for (let index = 0; index < 23; index++) {
    const requested = Math.round(random() * 140) + 20;
    const collected = Math.round(requested * (0.4 + random() * 0.5));
    const decided = random() > 0.7;
    const sku = SKUS[index % SKUS.length]!;

    rows.push([
      `ord-${index}`,
      `BUY-2026-${String(280 + index).padStart(5, "0")}`,
      sku.article,
      index % 2 === 0 ? "PICKING" : "CONTROL",
      String(requested),
      String(collected),
      String(requested - collected),
      reasons[index % reasons.length]!,
      random() > 0.6 ? cellCode((index % 40) + 1, (index % 9) + 1, (index % 29) + 1) : "",
      random() > 0.5 ? `CNT-${2000 + index}` : "",
      decided ? "SHIP_PARTIAL" : "",
      decided ? "Qolgani keyingi jo'natishga" : "",
      decided ? "DECIDED" : "WAITING_MANAGER",
      isoAgo(Math.round(random() * 3000) + 40),
      decided ? isoAgo(Math.round(random() * 400)) : "",
    ]);
  }

  return {
    columns: [
      "order_id", "order_number", "sku_article", "process",
      "requested_qty", "collected_qty", "remaining_qty", "waiting_reason",
      "blocked_locations", "count_task_ids", "manager_decision",
      "manager_note", "status", "detected_at", "decided_at",
    ],
    rows,
  };
}

export function kpiReport(): ReportFixture {
  const random = seeded(71);
  const rows: string[][] = [];

  for (let day = 0; day < 6; day++) {
    for (const worker of WORKERS) {
      const tasks = Math.round(random() * 22) + 8;
      rows.push([
        worker,
        new Date(Date.now() - day * 86_400_000).toISOString().slice(0, 10),
        TASK_TYPES[(day + worker.length) % TASK_TYPES.length]!,
        String(tasks),
        String(tasks * 2 + Math.round(random() * 10)),
        String(tasks * 14 + Math.round(random() * 60)),
        String(tasks * 180 + Math.round(random() * 900)),
        String(Math.round(random() * 1200)),
        String(Math.round(random() * 3)),
        String(Math.round(random() * 2)),
        // KPI-002: jarima faqat SANALADI — pul summasi ustuni
        // umuman mavjud emas.
        String(Math.round(random() * 1.4)),
      ]);
    }
  }

  return {
    columns: [
      "employee_id", "bucket_date", "task_type", "task_count", "line_count",
      "actual_qty", "work_seconds", "wait_seconds",
      "shortage_count", "excess_count", "penalty_count",
    ],
    rows,
  };
}

/**
 * ABC/XYZ tasnifi (P04-02).
 *
 * Taqsimot haqiqatga yaqin: A tovarlar asosan barqaror (X/Y),
 * C da esa tartibsiz va talabsiz tovarlar ko'p. Matritsa
 * (`abcXyzMatrix`) AYNI satrlardan sanaladi — ikki manba bir-biriga
 * zid ko'rinmasligi kerak.
 */
export function abcXyzReport(): ReportFixture {
  const random = seeded(97);
  const rows: string[][] = [];
  const classifiedAt = isoAgo(190);

  for (let index = 0; index < 42; index++) {
    const sku = SKUS[index % SKUS.length]!;
    const abc = index < 8 ? "A" : index < 19 ? "B" : "C";
    const roll = random();
    const noDemand = abc === "C" && roll > 0.82;
    const shortHistory = !noDemand && roll < 0.06;
    const variation =
      abc === "A" ? 0.04 + random() * 0.26 : abc === "B" ? 0.08 + random() * 0.45 : 0.18 + random() * 0.9;
    const xyz = noDemand || shortHistory ? "" : variation <= 0.1 ? "X" : variation <= 0.25 ? "Y" : "Z";
    const picks = noDemand ? 0 : Math.round((abc === "A" ? 900 : abc === "B" ? 180 : 25) * (0.4 + random()));
    const weekly = noDemand ? 0 : Math.round(picks * (2 + random() * 6) / 13);
    const periods = noDemand ? 13 : shortHistory ? 3 : 13;

    rows.push([
      String(4830000 + index * 37),
      sku.name,
      index % 3 === 0 ? "Oshxona anjomlari" : "Dasturxon",
      abc,
      xyz,
      abc + xyz,
      noDemand ? "NO_DEMAND" : shortHistory ? "SHORT_HISTORY" : "",
      String(picks),
      (weekly / 7).toFixed(3),
      noDemand ? "" : String(weekly),
      xyz ? variation.toFixed(4) : "",
      String(periods),
      String(noDemand ? 0 : Math.min(periods, 4 + Math.round(random() * 9))),
      classifiedAt,
    ]);
  }

  return {
    columns: [
      "sku_article", "sku_name", "product_group",
      "abc_class", "xyz_class", "abc_xyz", "xyz_note",
      "pick_count", "forecast_daily_qty",
      "period_mean_qty", "variation_coefficient",
      "period_count", "active_period_count", "classified_at",
    ],
    rows,
  };
}

export function abcXyzMatrix() {
  const { columns, rows } = abcXyzReport();
  const abcIndex = columns.indexOf("abc_class");
  const xyzIndex = columns.indexOf("xyz_class");
  const noteIndex = columns.indexOf("xyz_note");

  const counts = new Map<string, { abc_class: string; xyz_class: string; xyz_note?: string; sku_count: number }>();
  for (const row of rows) {
    const xyz = row[xyzIndex] || "NONE";
    const note = row[noteIndex] || undefined;
    const key = `${row[abcIndex]}|${xyz}|${note ?? ""}`;
    const cell = counts.get(key) ?? { abc_class: row[abcIndex]!, xyz_class: xyz, xyz_note: note, sku_count: 0 };
    cell.sku_count++;
    counts.set(key, cell);
  }

  return {
    cells: [...counts.values()],
    rule: {
      run_id: 41,
      run_at: isoAgo(190),
      abc_window_days: 90,
      abc_thresholds: { a: "0.8", b: "0.95" },
      xyz_period_days: 7,
      xyz_periods: 13,
      xyz_thresholds: { x: "0.1", y: "0.25" },
    },
  };
}

/**
 * Boshqaruv paneli agregatlari.
 *
 * Sonlar DAVRGA bog'liq: namuna rejimida ham «Bugun» va «30 kun»
 * bir xil chiqsa, davr tanlagich ishlamayotgandek ko'rinardi.
 * Ochiq topshiriqlar esa davrdan mustaqil — serverda ham shunday.
 */
export function dashboardSummary(
  days: number,
  tasks: Array<{ task_type: string; status: string }>,
  options?: { query?: Record<string, unknown> },
) {
  const scale = days === 1 ? 1 : days === 7 ? 6 : 23;
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  from.setDate(from.getDate() - (days - 1));

  // Ochiq kesim NAVBAT RO'YXATIDAN quriladi: ikkalasi alohida
  // yozilganda panel «Terish 15» deb ko'rsatar, navbat ekranida
  // esa ikkita qator turardi.
  const counts = new Map<string, number>();
  for (const task of tasks) {
    const key = `${task.task_type}|${task.status}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const open = [...counts.entries()].map(([key, count]) => {
    const [task_type, status] = key.split("|");
    // Har bir topshiriqda o'rtacha uchta tovar: son topshiriq
    // sonidan KO'P bo'lishi kerak, aks holda «SKU» ustuni
    // topshiriq sonini takrorlab, hech narsa qo'shmasdi.
    return {
      task_type: task_type!,
      status: status!,
      count,
      lines: count * 3,
      planned_qty: String(count * 3 * 14),
    };
  });

  const period = [
    { task_type: "PICK", created: 18, completed: 15, cancelled: 1, work_seconds: 9400 },
    { task_type: "PUTAWAY", created: 11, completed: 10, cancelled: 0, work_seconds: 5200 },
    { task_type: "RECEIPT", created: 6, completed: 5, cancelled: 0, work_seconds: 7100 },
    { task_type: "REPLENISHMENT", created: 7, completed: 6, cancelled: 0, work_seconds: 2600 },
    { task_type: "CONTROL", created: 9, completed: 9, cancelled: 0, work_seconds: 3100 },
    { task_type: "SHIPMENT", created: 4, completed: 4, cancelled: 0, work_seconds: 1800 },
  ].map((row) => ({
    task_type: row.task_type,
    created: row.created * scale,
    completed: row.completed * scale,
    cancelled: row.cancelled * scale,
    work_seconds: row.work_seconds * scale,
    lines: row.completed * 3 * scale,
    actual_qty: String(row.completed * 3 * 14 * scale),
  }));

  // Band xodimlar — namuna foydalanuvchilari (`usr-N`), ism
  // identity so'rovidan keladi.
  const busy = ["usr-0", "usr-2", "usr-4"];

  const podium = [
    { employee_id: "usr-0", task_count: 14, line_count: 52, actual_qty: "418.000", work_seconds: 11400, shortage_count: 0, penalty_count: 0 },
    { employee_id: "usr-3", task_count: 11, line_count: 47, actual_qty: "366.000", work_seconds: 9900, shortage_count: 1, penalty_count: 0 },
    { employee_id: "usr-1", task_count: 9, line_count: 31, actual_qty: "208.000", work_seconds: 8700, shortage_count: 0, penalty_count: 0 },
  ].map((row) => ({
    ...row,
    task_count: row.task_count * scale,
    line_count: row.line_count * scale,
    work_seconds: row.work_seconds * scale,
  }));

  // Solishtirish davri: sonlar biroz PASTROQ, shunda farq
  // ko'rsatkichi namunada ham ko'rinadi.
  const compareFrom = options?.query?.compare_from;
  const compareTo = options?.query?.compare_to;
  const compare =
    compareFrom && compareTo
      ? {
          from: String(compareFrom),
          to: String(compareTo),
          period: period.map((row) => ({
            ...row,
            created: Math.round(row.created * 0.85),
            completed: Math.round(row.completed * 0.82),
          })),
        }
      : undefined;

  // Kasb bo'yicha liderlar: har bir turda lider va uni quvib
  // kelayotgan xodim. Ismlar `usr-N` — identity so'rovidan keladi.
  const leaderSpecs = [
    { type: "PICK", first: "usr-0", second: "usr-3", tasks: 14, lines: 52, work: 11400, shortage: 0 },
    { type: "RECEIPT", first: "usr-1", second: "usr-4", tasks: 9, lines: 27, work: 8100, shortage: 1 },
    { type: "PUTAWAY", first: "usr-2", second: "usr-0", tasks: 8, lines: 19, work: 6600, shortage: 0 },
    { type: "CONTROL", first: "usr-4", second: "usr-1", tasks: 6, lines: 22, work: 4800, shortage: 0 },
    { type: "COUNT", first: "usr-3", second: "", tasks: 4, lines: 16, work: 3300, shortage: 0 },
  ];
  const leaders = leaderSpecs.flatMap((spec) => {
    const rows = [
      {
        task_type: spec.type, rank: 1, employee_id: spec.first,
        task_count: spec.tasks * scale, line_count: spec.lines * scale,
        actual_qty: String(spec.tasks * 32), work_seconds: spec.work * scale,
        shortage_count: spec.shortage, penalty_count: 0,
      },
    ];
    if (spec.second) {
      rows.push({
        task_type: spec.type, rank: 2, employee_id: spec.second,
        task_count: Math.max(Math.round(spec.tasks * 0.6), 1) * scale,
        line_count: Math.round(spec.lines * 0.6) * scale,
        actual_qty: String(Math.round(spec.tasks * 0.6) * 32),
        work_seconds: Math.round(spec.work * 0.6) * scale,
        shortage_count: 0, penalty_count: 0,
      });
    }
    return rows;
  });

  return {
    from: from.toISOString(),
    to: new Date().toISOString(),
    open,
    period,
    busy_employee_ids: busy,
    podium,
    leaders,
    compare,
  };
}

/**
 * Ombor to'ldirilishi.
 *
 * Sonlar O'ZARO MOS: vazifa kesimining yig'indisi zona kesimining
 * yig'indisiga teng, chunki ikkalasi ham bitta ombordagi bitta
 * qoldiqni boshqa-boshqa kesimda ko'rsatadi. Alohida yozilganda
 * dumaloq diagramma bilan jadval bir-biriga mos kelmasdi.
 */
export function warehouseFill() {
  // Vazifa kesimi — ombor bo'ylab oqim tartibida.
  const functions = [
    { key: "RECEIPT", qty: 1840, lp: 12, sku: 26, capacity: 40, locations: 10 },
    { key: "MX", qty: 48350, lp: 214, sku: 412, capacity: 320, locations: 160 },
    { key: "OX", qty: 9120, lp: 96, sku: 338, capacity: 120, locations: 120 },
    { key: "CONTROL", qty: 640, lp: 6, sku: 14, capacity: 16, locations: 6 },
    { key: "SHIPPING", qty: 2460, lp: 18, sku: 41, capacity: 32, locations: 8 },
  ];

  // Zona kesimi — o'sha qoldiq, korpuslar bo'yicha.
  const zones = [
    { key: "A-STORAGE", warehouse: "A", qty: 31200, lp: 168, sku: 402, capacity: 260, locations: 170 },
    { key: "B-STORAGE", warehouse: "A", qty: 18900, lp: 104, sku: 221, capacity: 150, locations: 82 },
    { key: "C-STORAGE", warehouse: "A", qty: 9370, lp: 56, sku: 118, capacity: 70, locations: 38 },
    { key: "DOCK", warehouse: "A", qty: 2300, lp: 12, sku: 30, capacity: 32, locations: 8 },
  ];

  // ABC zonalari — to'ldirilish foizi uchun; biri to'lgan.
  const abcZones = [
    { key: "A", qty: 22400, lp: 112, sku: 140, capacity: 120, locations: 60 },
    { key: "B", qty: 19800, lp: 98, sku: 206, capacity: 160, locations: 80 },
    { key: "C", qty: 16870, lp: 74, sku: 310, capacity: 180, locations: 90 },
    { key: "—", qty: 2700, lp: 56, sku: 115, capacity: 0, locations: 60 },
  ];

  const row = (item: {
    key: string;
    warehouse?: string;
    qty: number;
    lp: number;
    sku: number;
    capacity: number;
    locations: number;
  }) => ({
    key: item.key,
    warehouse_code: item.warehouse ?? "",
    qty: String(item.qty),
    lp_count: item.lp,
    sku_count: item.sku,
    capacity_slots: item.capacity,
    location_count: item.locations,
  });

  return {
    functions: functions.map(row),
    zones: zones.map(row),
    abc_zones: abcZones.map(row),
  };
}

export { SKUS, WORKERS, cellCode, lpCode, isoAgo, seeded };

/**
 * Pallet rejasi namunasi (OPT-03…05). Server algoritmiga o'xshash:
 * 40×30 korobka qatlamda 8 ta (300×400 yo'nalishda), og'ir pastda,
 * mo'rt eng yuqori qatlamda; bitta SKU da VGH yo'q — reja «ishonchli
 * emas».
 */
export function palletPlanFixture(orderId: string) {
  const legend = [
    { key: "sku-pl", code: "PL-2601", name: "Likopcha 26 sm", color: "#0072B2", boxes: 26, units_per_box: "12",
      length_mm: 400, width_mm: 300, height_mm: 250, weight_g: 12000, fragile: false, no_stack: false },
    { key: "sku-ks", code: "KS-1402", name: "Kosa 14 sm", color: "#E69F00", boxes: 12, units_per_box: "24",
      length_mm: 400, width_mm: 300, height_mm: 220, weight_g: 8000, fragile: false, no_stack: false },
    { key: "sku-gl", code: "GL-0300", name: "Stakan 300 ml", color: "#009E73", boxes: 6, units_per_box: "36",
      length_mm: 400, width_mm: 300, height_mm: 200, weight_g: 4000, fragile: true, no_stack: false },
  ];
  const limits = { length_mm: 1200, width_mm: 800, max_height_mm: 1800, base_height_mm: 150, max_weight_g: 1_000_000 };
  const slots = Array.from({ length: 8 }, (_, i) => ({ x: (i % 4) * 300, y: Math.floor(i / 4) * 400 }));
  const queue = legend.flatMap((item) => Array.from({ length: item.boxes }, () => item));

  const pallets: Array<Record<string, unknown>> = [];
  let index = 0;
  while (index < queue.length) {
    const layers: Array<Record<string, unknown>> = [];
    let z = 0;
    let weight = 0;
    const counts = new Map<string, number>();
    while (index < queue.length) {
      const first = queue[index]!;
      const top = first.fragile;
      if (z + first.height_mm > limits.max_height_mm - limits.base_height_mm) break;
      const boxes = [];
      let height = 0;
      let layerWeight = 0;
      for (const slot of slots) {
        const item = queue[index];
        if (!item || item.fragile !== top) break;
        boxes.push({ key: item.key, code: item.code, x_mm: slot.x, y_mm: slot.y, z_mm: z, length_mm: 300,
          width_mm: 400, height_mm: item.height_mm, weight_g: item.weight_g, rotated: true, top_only: item.fragile });
        counts.set(item.key, (counts.get(item.key) ?? 0) + 1);
        height = Math.max(height, item.height_mm);
        layerWeight += item.weight_g;
        index++;
      }
      layers.push({ index: layers.length + 1, z_mm: z, height_mm: height, weight_g: layerWeight, top_only: top, boxes });
      z += height;
      weight += layerWeight;
      if (top) break;
    }
    pallets.push({
      index: pallets.length + 1, height_mm: limits.base_height_mm + z, weight_g: weight,
      boxes: layers.reduce((sum, layer) => sum + (layer.boxes as unknown[]).length, 0), layers,
      skus: legend.filter((item) => counts.has(item.key))
        .map((item) => ({ key: item.key, code: item.code, name: item.name, boxes: counts.get(item.key)! })),
    });
  }

  return {
    order_id: orderId,
    order_number: orderId === "ord-2" ? "BUY-24-0412" : "BUY-24-0415",
    customer_name: "Oila savdo MChJ",
    status: "ALLOCATED",
    plan: {
      limits,
      legend,
      pallets,
      reliable: false,
      total_boxes: queue.length,
      missing: [{ key: "sku-ch", code: "CH-0100", name: "Choynak 1 l", boxes: 4, fields: ["height_cm", "weight_kg"] }],
      unplaced: [],
    },
  };
}

export function pickingOrdersFixture() {
  return [
    { id: "mock-so-1", order_number: "РН-000501", external_id: "РН-000501", customer_id: "cp-2", warehouse_code: "A",
      recipient_warehouse: "Склад Самарканд",
      status: "CREATED", priority: 0, version: 1, created_at: isoAgo(12), lp_code: "" },
    { id: "ord-1", order_number: "BUY-24-0415", customer_id: "cp-3", customer_name: "Oila savdo MChJ", warehouse_code: "A",
      recipient_warehouse: "Склад Бухара", status: "ALLOCATED", version: 3, created_at: isoAgo(35), lp_code: "LP-000184213",
      closed_in_1c_at: isoAgo(10), closed_in_1c_reason: "ISSUE_ORDER", closed_in_1c_ref: "НФGD-011562" },
    { id: "ord-2", order_number: "BUY-24-0412", customer_id: "cp-4", customer_name: "Nur market", warehouse_code: "A",
      recipient_warehouse: "", status: "PICKED", version: 5, created_at: isoAgo(140), lp_code: "LP-000184214" },
  ];
}

/** RPT-03 namunalari: har kesimda bir necha tushunarli satr. */
export function qualityReports(): Record<string, () => { columns: string[]; rows: string[][] }> {
  return {
    "data-quality": () => ({
      columns: ["sku_article", "sku_name", "product_group", "has_complete_vgh", "has_photo", "has_barcode",
        "length_cm", "width_cm", "height_cm", "weight_kg", "ikpu_code", "tnved_code", "issues"],
      rows: [
        ["CH-0100", "Choynak 1 l", "Oshxona", "false", "true", "true", "22", "18", "0", "0", "", "6911100000",
          "VGH_MISSING;WEIGHT_MISSING;IKPU_MISSING"],
        ["GL-0300", "Stakan 300 ml", "Shisha", "true", "false", "true", "40", "30", "20", "4", "07013001001000000", "",
          "PHOTO_MISSING;TNVED_MISSING"],
      ],
    }),
    "customs-stock": () => ({
      columns: ["location_code", "warehouse_code", "zone_code", "location_function", "sku_article", "sku_name",
        "lp_id", "lot_ref", "on_hand_qty", "reserved_qty", "last_movement_at", "held_days"],
      rows: [["T014", "", "T", "CUSTOMS", "PL-2601", "Likopcha 26 sm", "", "", "240", "0", isoAgo(60 * 24 * 9), "9"]],
    }),
    "sku-without-ox": () => ({
      columns: ["sku_article", "sku_name", "abc_class", "on_hand_qty", "mx_qty", "other_qty", "location_count",
        "last_movement_at"],
      rows: [["KS-1402", "Kosa 14 sm", "A", "480", "480", "0", "2", isoAgo(120)]],
    }),
    "abc-zone-mismatch": () => ({
      columns: ["location_code", "location_function", "location_abc_zone", "sku_article", "sku_name",
        "sku_abc_class", "lp_id", "on_hand_qty", "direction"],
      rows: [["A01-48-05-12", "MX", "C", "KS-1402", "Kosa 14 sm", "A", "", "240", "CLOSER"]],
    }),
    "lp-misplaced": () => ({
      columns: ["lp_id", "reason", "location_code", "location_function", "sku_article", "sku_name",
        "quality_status", "on_hand_qty", "location_count", "last_movement_at", "idle_hours"],
      rows: [["LP00004121", "STUCK_IN_TRANSIT_AREA", "JOY-014", "BUFFER", "PL-2601", "Likopcha 26 sm",
        "AVAILABLE", "96", "1", isoAgo(60 * 31), "31"]],
    }),
    "slow-moving": () => ({
      columns: ["location_code", "warehouse_code", "location_function", "sku_article", "sku_name", "lp_id",
        "quality_status", "on_hand_qty", "last_movement_at", "idle_days", "idle_bucket"],
      rows: [["C114", "C", "MX", "VZ-0900", "Vaza 90 sm", "", "AVAILABLE", "12", isoAgo(60 * 24 * 95), "95", "90+"]],
    }),
  };
}
